/**
 * Built-in framework data: socialEmotional, executiveFunction, creative.
 *
 * Every level descriptor is written so a parent can settle it by watching for
 * two minutes. No inference, no comparison to other children, no clinical
 * language. Where the family's own rubric recorded a real observation, that
 * observation is the exemplar.
 *
 * Research anchors used here:
 *  - Parten (1932) social participation; Howes peer-play scale — cooperativePlay
 *  - Kopp (1989) self-regulation; Rothbart effortful control — emotionRegulation
 *  - Denham / Ridgeway emotion lexicon — emotionVocabulary
 *  - Hoffman empathic distress; Wellman desire-before-belief — empathy
 *  - Fehr et al. (2008) early egalitarian sharing — sharing
 *  - Diamond (2013) core executive functions; Zelazo DCCS — workingMemory, inhibition
 *  - Kochanska day/night and tapping tasks — inhibition
 *  - Lowenfeld scribble → preschematic → schematic — drawing skills
 *  - Luquet intellectual vs visual realism; Freeman & Janikoun (1972) — observationalDrawing
 *  - Goodenough-Harris Draw-A-Person part counts — faceDrawingFrontal
 *  - Piaget symbolic play; Vygotsky play as the zone of proximal development;
 *    Smilansky sociodramatic criteria — pretendPlay
 *  - Moog / Davidson melodic contour; Provasi & Bobin-Bègue beat entrainment — music
 *  - Johnson block stages (tower → bridge → enclosure) — building
 *  - Clay concepts about print; Sulzby emergent storybook reading; Gentry
 *    invented spelling — bookMaking
 */

import type {
  DomainDef,
  DomainId,
  DomainKey,
  FrameworkId,
  Level,
  LevelDescriptor,
  Skill,
  SkillId,
} from '../types'

// ─────────────────────────────────────────────────────────────
// Id construction
// ─────────────────────────────────────────────────────────────

/** The built-in framework these definitions belong to. */
const FRAMEWORK_ID = 'framework.aurora.builtin.v1' as FrameworkId

const domainId = (key: DomainKey): DomainId => `domain.${key}` as DomainId

const skillId = (domainKey: DomainKey, key: string): SkillId =>
  `${domainKey}.${key}` as SkillId

const se = (key: string): SkillId => skillId('socialEmotional', key)
const ef = (key: string): SkillId => skillId('executiveFunction', key)
const cr = (key: string): SkillId => skillId('creative', key)

// ─────────────────────────────────────────────────────────────
// Authoring helper — guarantees exactly 5 descriptors, ids, ordering
// ─────────────────────────────────────────────────────────────

type LevelSpec = Omit<LevelDescriptor, 'level'>

/** Exactly five, in level order 1..5. The tuple type is the guarantee. */
type FiveLevels = readonly [LevelSpec, LevelSpec, LevelSpec, LevelSpec, LevelSpec]

interface SkillSpec {
  key: string
  name: string
  description: string
  typicalMonths?: readonly [number, number]
  prerequisites?: SkillId[]
  assessmentHints: string[]
  levels: FiveLevels
}

const defineSkill = (
  domainKey: DomainKey,
  sortOrder: number,
  spec: SkillSpec,
): Skill => ({
  _id: skillId(domainKey, spec.key),
  domainId: domainId(domainKey),
  domainKey,
  key: spec.key,
  name: spec.name,
  description: spec.description,
  levelDescriptors: spec.levels.map((l, i) => ({
    level: (i + 1) as Level,
    descriptor: l.descriptor,
    exemplars: [...l.exemplars],
  })),
  typicalMonths: spec.typicalMonths,
  prerequisites: spec.prerequisites ?? [],
  assessmentHints: [...spec.assessmentHints],
  sortOrder,
})

const defineDomain = (
  key: DomainKey,
  name: string,
  description: string,
  iconKey: string,
  sortOrder: number,
): DomainDef => ({
  _id: domainId(key),
  frameworkId: FRAMEWORK_ID,
  key,
  name,
  description,
  colorToken: `domain.${key}`,
  iconKey,
  sortOrder,
})

// ─────────────────────────────────────────────────────────────
// Domains
// ─────────────────────────────────────────────────────────────

export const SOCIAL_EMOTIONAL_DOMAIN: DomainDef = defineDomain(
  'socialEmotional',
  'Social & Emotional',
  'Naming feelings, steadying them, and using them well with other people — comforting, caring for someone smaller, waiting, sharing, and working out a disagreement.',
  'heart-handshake',
  6,
)

export const EXECUTIVE_FUNCTION_DOMAIN: DomainDef = defineDomain(
  'executiveFunction',
  'Executive Function & Attention',
  'Holding a plan in mind and carrying it out: staying with something, remembering the steps, stopping an impulse, and finishing what was started. Attention is tracked as two separate skills, because interest changes it completely.',
  'brain-circuit',
  7,
)

export const CREATIVE_DOMAIN: DomainDef = defineDomain(
  'creative',
  'Creative Expression',
  'Making things and meaning them: drawing, building, pretending, music, and putting it all together into books she can read aloud.',
  'palette',
  8,
)

// ─────────────────────────────────────────────────────────────
// SOCIAL & EMOTIONAL
// ─────────────────────────────────────────────────────────────

export const SOCIAL_EMOTIONAL_SKILLS: Skill[] = [
  defineSkill('socialEmotional', 1, {
    key: 'emotionVocabulary',
    name: 'Emotion Vocabulary',
    description:
      'The words she has for feelings — hers and other people\'s. Naming a feeling is what makes it steerable, so this feeds regulation, empathy and negotiation.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'While reading, stop on a picture and ask "how is he feeling?" — count how many different words come up across a week, not in one sitting.',
      'After a wobble has passed, ask "what was that feeling called?" and see whether she supplies a word without being given choices.',
      'Offer a near pair in play — "was she angry or was she frustrated?" — and listen for whether she treats them as different.',
    ],
    levels: [
      {
        descriptor:
          'Matches happy and sad faces in a book or on cards, and uses one feeling word or sign about herself.',
        exemplars: [
          'Points to the crying child in a picture book when asked "who is sad?"',
          'Says "happy" while smiling at a favorite song.',
        ],
      },
      {
        descriptor:
          'Names three or four feelings — happy, sad, mad, scared — in herself and in pictures.',
        exemplars: [
          'Says "I mad" while pushing a puzzle away.',
          'Labels a storybook wolf as "scary" and the lost duckling as "sad".',
        ],
      },
      {
        descriptor:
          'Names six to eight feelings including excited, proud, frustrated and tired, and applies them to herself in the moment they are happening.',
        exemplars: [
          'Says "I am frustrated" while the lid will not go on, instead of only crying.',
          'Says "I am proud of my tower" without being prompted with the word.',
        ],
      },
      {
        descriptor:
          'Tells apart neighboring feelings — frustrated from angry, shy from scared, disappointed from sad — and gives the cause: "she is frustrated because it will not fit".',
        exemplars: [
          'Corrects an adult: "not sad, disappointed — I wanted the park."',
          'Says the baby is "shy, not scared" of a new visitor.',
        ],
      },
      {
        descriptor:
          'Talks about feelings that changed or two at once, and about a character feeling something different from what she feels herself.',
        exemplars: [
          'Says "I was scared on the slide and then I was proud."',
          'Says "I like the dark but my cousin does not like it."',
        ],
      },
    ],
  }),

  defineSkill('socialEmotional', 2, {
    key: 'emotionRegulation',
    name: 'Emotion Regulation',
    description:
      'What she does with a strong feeling once it arrives — how fast she comes back, and how much of the recovery is hers. Words carry most of the work at this age.',
    typicalMonths: [30, 36],
    prerequisites: [se('emotionVocabulary')],
    assessmentHints: [
      'Time the recovery, not the upset: after a small disappointment, notice how long until she is back in play, and whether an adult had to do it.',
      'Watch which strategy she reaches for unprompted — walking away, asking for help, breathing, telling you the problem.',
      'Notice whether the words arrive during the feeling or only afterwards; during is the newer skill.',
    ],
    levels: [
      {
        descriptor:
          'Settles inside a couple of minutes with an adult doing the settling — picked up, held, handed a familiar object.',
        exemplars: [
          'Calms when carried to another room and given her comfort toy.',
        ],
      },
      {
        descriptor:
          'Gets a word or a sign out during the upset ("mad!") and takes a swap an adult offers — a different cup, a turn later — with the adult right beside her.',
        exemplars: [
          'Says "mine!" then accepts the second digger when it is put in her hand.',
        ],
      },
      {
        descriptor:
          'Comes back from a small disappointment in about two minutes, mostly with words rather than a big upset, after one calming prompt.',
        exemplars: [
          'Says "I wanted the blue one" and takes the green one after a short pause.',
          'Rarely melts down; the upset stays verbal.',
        ],
      },
      {
        descriptor:
          'Names both the feeling and its cause while she is still in it, and picks a strategy she has been taught — a breath, moving away, asking for help.',
        exemplars: [
          'Says "I am frustrated because the tower fell" and then asks for a hand rebuilding it.',
          'Walks to the couch on her own to sit for a moment, then returns.',
        ],
      },
      {
        descriptor:
          'Catches the feeling on the way up and steadies herself with no prompt, waits several minutes for something she badly wants, and returns to play after a real loss such as a broken toy.',
        exemplars: [
          'Says "I need a minute" and takes it.',
          'Waits through the whole timer for a turn without an adult holding the line.',
        ],
      },
    ],
  }),

  defineSkill('socialEmotional', 3, {
    key: 'empathy',
    name: 'Empathy',
    description:
      'Noticing what someone else feels and doing something that fits them rather than what would comfort her. The step from her own comfort object to theirs is the real marker.',
    typicalMonths: [30, 36],
    prerequisites: [se('emotionVocabulary')],
    assessmentHints: [
      'When someone in the house is hurt or sad, hang back three seconds before reacting and watch what she does first.',
      'Read a story and ask what would help the character — listen for a fix aimed at the character, not at her.',
      'Notice whether she adjusts her volume or speed near a sleeping baby without being told.',
    ],
    levels: [
      {
        descriptor:
          'Notices distress and reacts to it — stops, stares, fetches an adult, or becomes upset herself.',
        exemplars: ['Freezes and looks at a crying child across the playground.'],
      },
      {
        descriptor:
          'Offers comfort using what comforts her, bringing her own blanket or toy to the person who is upset.',
        exemplars: ['Puts her own pacifier-sized bear into a crying cousin\'s lap.'],
      },
      {
        descriptor:
          'Reads the feeling and matches the comfort to that person — pats, hugs, brings their item — and checks in with a question.',
        exemplars: [
          'Brings the baby the baby\'s own blanket rather than her own.',
          'Asks "are you okay?" after someone bumps a knee.',
        ],
      },
      {
        descriptor:
          'Says why the person feels that way, predicts what would help, and changes her own behavior to suit them.',
        exemplars: [
          'Says "he is crying because his mom went out — he wants a cuddle."',
          'Lowers her voice on her own when the baby is asleep.',
        ],
      },
      {
        descriptor:
          'Spots a feeling that is not loud — someone left out, someone gone quiet — and acts before anyone asks, understanding the person can want something she does not want.',
        exemplars: [
          'Hands a second shovel to the child standing at the edge of the sandbox.',
          'Says "you do not like tomatoes, I will take them."',
        ],
      },
    ],
  }),

  defineSkill('socialEmotional', 4, {
    key: 'caregiving',
    name: 'Caregiving',
    description:
      'Taking responsibility for someone smaller — a baby, a younger cousin, a doll — with gentleness and attention to their state. A standout strength for this child, and the strongest channel to route other learning through: she reads to the baby, she teaches the doll, she runs the clinic.',
    typicalMonths: [30, 36],
    prerequisites: [se('empathy')],
    assessmentHints: [
      'Give her one real job with a younger child — hand out the snack, hold the book — and watch how long she stays with it and whether she watches their face.',
      'Ask her to teach the doll something she has just learned, and listen for whether she breaks it into steps and waits.',
      'During a visit with younger cousins, count the moments she adjusts for them unprompted: slower, softer, handing back a dropped toy.',
    ],
    levels: [
      {
        descriptor:
          'Carries a doll or animal around, holds it, and gives it a token bite or drink.',
        exemplars: ['Tips a toy bottle to a bear\'s mouth for a second, then moves on.'],
      },
      {
        descriptor:
          'Plays out one care routine on a doll — feeding, covering with a blanket, rocking — copying what has been done to her.',
        exemplars: ['Tucks the doll in and pats its back the way she is patted.'],
      },
      {
        descriptor:
          'Runs a short care sequence of two or three steps for a doll or a real younger child, and adjusts to their signals: soft voice, gentle hands, gives back what they dropped.',
        exemplars: [
          'Picks up the rattle a baby cousin dropped and puts it back in reach.',
          'Softens her voice for the baby without being asked.',
        ],
      },
      {
        descriptor:
          'Holds a caregiving role for several minutes with a real younger child — reading aloud to them, passing the right piece, steering them off a hazard — and reports on their state.',
        exemplars: [
          'Reads a book aloud to a younger cousin, turning the pages for them.',
          'Says "she is tired, she needs her nap."',
          'Gentle and aware with younger cousins across a whole visit.',
        ],
      },
      {
        descriptor:
          'Teaches: shows a younger child how to do something, breaks it into steps, waits while they try, and celebrates their success. Keeps the care going across a long stretch.',
        exemplars: [
          'Shows a cousin where the puzzle piece goes, then pulls her hand back so they can place it.',
          'Says "you did it!" when the younger child manages it.',
        ],
      },
    ],
  }),

  defineSkill('socialEmotional', 5, {
    key: 'turnTaking',
    name: 'Turn Taking',
    description:
      'Holding the order in a shared activity: waiting through someone else\'s turn and handing over at the end of hers. The mechanical half of cooperative play.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Play a rolling-ball or dice game and count rounds before she needs a reminder.',
      'Add a third player and see whether the order survives — three is a genuinely harder problem than two.',
      'Interrupt a game mid-round, then ask "whose turn was it?" and see whether she has kept it.',
    ],
    levels: [
      {
        descriptor:
          'Passes an object back and forth with an adult for two or three exchanges before the game breaks down.',
        exemplars: ['Rolls a ball back three times, then wanders off with it.'],
      },
      {
        descriptor:
          'Takes her turn in a two-person game when an adult names it, waiting a few seconds for theirs.',
        exemplars: ['Waits while an adult says "my turn — now yours" and hands the dice over.'],
      },
      {
        descriptor:
          'Waits through four to six rounds of a simple game with about one reminder, and hands over the piece when reminded.',
        exemplars: [
          'Plays a short matching game to the end, needing prompts at the handover.',
        ],
      },
      {
        descriptor:
          'Keeps the order in a game of three or more people without reminders, and tells others whose turn it is.',
        exemplars: ['Says "now it is Daddy, then me" and holds it for a whole game.'],
      },
      {
        descriptor:
          'Holds the order in unstructured group play, waits through a long turn without complaint, and restarts the order correctly after an interruption.',
        exemplars: ['Comes back from a snack break and says "we stopped at Nina."'],
      },
    ],
  }),

  defineSkill('socialEmotional', 6, {
    key: 'sharing',
    name: 'Sharing',
    description:
      'Giving up part of something she has right now. Different from turn taking, because the cost is immediate rather than deferred; fair splitting arrives noticeably later.',
    typicalMonths: [34, 42],
    prerequisites: [se('turnTaking')],
    assessmentHints: [
      'Hand her a bowl of something good with another child present, and watch what happens before you say anything.',
      'Notice the difference between giving away what she is not using and giving away what is in her hand — only the second one counts here.',
      'Ask her to split three items between two people and listen to how she reasons about the odd one.',
    ],
    levels: [
      {
        descriptor: 'Hands an object to an adult who asks for it, at least some of the time.',
        exemplars: ['Gives the spoon back when asked, on the second ask.'],
      },
      {
        descriptor:
          'Lets another child use something she is not holding, and holds on to whatever is in her hands.',
        exemplars: ['Ignores a cousin taking a spare block; protests over the one she is placing.'],
      },
      {
        descriptor:
          'Gives up a wanted item once an adult sets up a swap or a timer — a wobble, but no long protest.',
        exemplars: ['Hands over the scooter when the timer goes, with one sigh.'],
      },
      {
        descriptor:
          'Offers a share before being asked — splitting the crackers, handing over one of two markers — and waits while the other person has it.',
        exemplars: ['Breaks the cookie in half and gives half to a cousin unprompted.'],
      },
      {
        descriptor:
          'Divides fairly on her own initiative, sometimes gives away the better piece, and can say why the split worked.',
        exemplars: [
          'Deals out "one for you, one for me, one for the baby" and checks the counts.',
        ],
      },
    ],
  }),

  defineSkill('socialEmotional', 7, {
    key: 'cooperativePlay',
    name: 'Cooperative Play',
    description:
      'Playing with another child toward a shared goal rather than beside them. This is the one skill a home setting cannot manufacture — it needs real peers, repeatedly.',
    typicalMonths: [34, 44],
    prerequisites: [se('turnTaking')],
    assessmentHints: [
      'At a playground or a visit, time the longest stretch where both children are working on the same thing rather than two things.',
      'Watch what happens when the shared play breaks down — does she repair it, or does it end?',
      'Listen for role language ("you be the patient"), which marks the shift from side by side to together.',
    ],
    levels: [
      {
        descriptor:
          'Plays beside another child with the same materials, watching them but keeping her own project.',
        exemplars: ['Builds her own tower next to a cousin building theirs, glancing over.'],
      },
      {
        descriptor:
          'Brief joint moments — passing a block to the other builder, copying their action — then back to her own play.',
        exemplars: ['Hands over a piece, then returns to her own construction.'],
      },
      {
        descriptor:
          'Plays a shared game with one goal for several minutes — one tower built together, both feeding the same doll — with an adult keeping it going.',
        exemplars: [
          'Starting to play with rather than beside, with cues on turn taking during games.',
        ],
      },
      {
        descriptor:
          'Keeps cooperative play going for ten minutes or more, hands out roles, and fixes a small breakdown by suggesting something.',
        exemplars: [
          'Says "you be the patient and I will be the doctor" and the game holds.',
          'Says "we can both build the roof" when two hands collide.',
        ],
      },
      {
        descriptor:
          'Plans the game with others before it starts, agrees the rules, keeps three or more players going, and brings in a child who is on the outside.',
        exemplars: ['Says "first we make the zoo, then the animals come" and the group follows.'],
      },
    ],
  }),

  defineSkill('socialEmotional', 8, {
    key: 'negotiation',
    name: 'Negotiation',
    description:
      'Getting to an outcome both people can live with, using words. Starts as stating a want to an adult and ends as a deal she brokers herself.',
    typicalMonths: [36, 46],
    prerequisites: [se('cooperativePlay'), se('emotionVocabulary')],
    assessmentHints: [
      'When two children want the same thing, wait five seconds before stepping in and see what she offers.',
      'Listen for a proposal aimed at the other child rather than a complaint aimed at you.',
      'Notice whether she ever volunteers to go second — taking the worse half of her own deal is the advanced move.',
    ],
    levels: [
      {
        descriptor:
          'Protests with a sound or one word when something is taken — "no" or "mine".',
        exemplars: ['Shouts "mine!" and holds the toy tighter.'],
      },
      {
        descriptor:
          'Tells an adult what she wants in words and lets the adult sort it out.',
        exemplars: ['Comes over and says "I want the red bucket."'],
      },
      {
        descriptor:
          'Makes a direct proposal to the other person — "can I have a turn after you?" — and accepts a compromise an adult offers.',
        exemplars: ['Asks a cousin "my turn next?" instead of grabbing.'],
      },
      {
        descriptor:
          'Offers a trade or an alternative herself, and takes her turn at being the one who gives way.',
        exemplars: ['Says "you can have the truck if I get the digger."'],
      },
      {
        descriptor:
          'Reaches a plan both can accept with no adult involved, states both wants out loud, and sticks to the agreement afterwards.',
        exemplars: [
          'Says "you want the doctor and I want the doctor, so I go first and then you" — and hands it over when the time comes.',
        ],
      },
    ],
  }),

  defineSkill('socialEmotional', 9, {
    key: 'handlingDisappointment',
    name: 'Handling Disappointment',
    description:
      'What happens when the answer is no, the plan is cancelled, or she loses. Distinct from general regulation because the trigger is a decision she cannot change.',
    typicalMonths: [36, 46],
    prerequisites: [se('emotionRegulation')],
    assessmentHints: [
      'Play a game where an adult sometimes wins, and watch the moment of losing rather than the game.',
      'Cancel a small pleasant thing with a clear reason, then watch whether she asks for the alternative herself.',
      'Notice whether her upset spreads to the other people in the room or stays with her.',
    ],
    levels: [
      {
        descriptor:
          'Cries or protests at "no" and needs an adult to move her on to something else.',
        exemplars: ['Melts at the shop door and settles once carried to the car.'],
      },
      {
        descriptor:
          'Takes a "not now" when an adult names what happens instead, after a short protest.',
        exemplars: ['Accepts "after lunch" when lunch is pointed at.'],
      },
      {
        descriptor:
          'Takes losing a game or hearing a "no" with a short upset that ends inside a minute or so, then rejoins.',
        exemplars: ['Says "aw" when the adult wins, then sets the pieces up again.'],
      },
      {
        descriptor:
          'Says the feeling and the reason at the moment of the "no", and asks for the alternative herself.',
        exemplars: ['Says "I am sad because I wanted the park. Can we go tomorrow?"'],
      },
      {
        descriptor:
          'Loses and congratulates the winner, or hears a plan cancelled and offers her own second plan, keeping the feeling without spoiling it for everyone else.',
        exemplars: [
          'Says "you won! Play again?"',
          'Says "the rain came, so we can do the clinic inside."',
        ],
      },
    ],
  }),

  defineSkill('socialEmotional', 10, {
    key: 'independence',
    name: 'Independence in Play',
    description:
      'Choosing her own activity and staying with it without an adult running it. This is the engine of self-directed learning, and already a strength here.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Step into the next room during her play and time how long before she comes to find you.',
      'Notice who chooses the activity: a self-chosen project counts, an adult-set one does not.',
      'Watch what she does at the first snag — solves it, changes the plan, or fetches an adult.',
    ],
    levels: [
      {
        descriptor:
          'Plays alone for a couple of minutes with an adult in sight, checking back often.',
        exemplars: ['Stacks blocks for two minutes, looking up between each one.'],
      },
      {
        descriptor:
          'Keeps a self-chosen game going for five to ten minutes in the same room while an adult does something else.',
        exemplars: ['Plays with the animals while an adult cooks in the same room.'],
      },
      {
        descriptor:
          'Chooses the activity, gets the materials out herself, and plays fifteen minutes or more with no adult direction, including from the next room.',
        exemplars: ['Fetches paper and pens and draws without being set up.'],
      },
      {
        descriptor:
          'Starts and finishes a project she set herself, tells an adult about it afterwards, and asks for help only when genuinely stuck.',
        exemplars: [
          'Self-directed play is her default; she narrates the finished drawing afterwards.',
        ],
      },
      {
        descriptor:
          'Runs a long self-directed stretch of half an hour or more, changes activity herself when done, clears part of it away, and handles a small frustration alone rather than fetching an adult.',
        exemplars: ['Moves from drawing to building on her own and puts the pens back first.'],
      },
    ],
  }),
]

// ─────────────────────────────────────────────────────────────
// EXECUTIVE FUNCTION & ATTENTION
// ─────────────────────────────────────────────────────────────

export const EXECUTIVE_FUNCTION_SKILLS: Skill[] = [
  defineSkill('executiveFunction', 1, {
    key: 'attentionPreferred',
    name: 'Attention on Preferred Activities',
    description:
      'How long she stays with something she chose and enjoys. Tracked separately from attention on adult-chosen work, because at this age the two move independently — interest, not effort, sets the length.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Time one favorite activity end to end without interrupting, and record the number rather than an impression.',
      'Notice whether she asks to come back to it later — returning is a stronger signal than one long sitting.',
      'Watch what a small background noise does; ignoring it is part of the skill.',
    ],
    levels: [
      {
        descriptor:
          'Stays with a favorite activity three to five minutes, then moves on to the next thing.',
        exemplars: ['Four minutes with the shape sorter before switching.'],
      },
      {
        descriptor:
          'Five to ten minutes on something she chose, with an adult nearby.',
        exemplars: ['Eight minutes of animal play while an adult sits alongside.'],
      },
      {
        descriptor:
          'Ten to fifteen minutes on a chosen activity, holding through small background noise.',
        exemplars: ['Finishes a whole puzzle with the washing machine running.'],
      },
      {
        descriptor:
          'Twenty minutes or more on a chosen activity, and asks to return to it later.',
        exemplars: [
          'Twenty-plus minutes on work that interests her.',
          'Thirty minutes with books in one sitting.',
        ],
      },
      {
        descriptor:
          'Half an hour or more on a self-set project, comes back to unfinished work on another day, and pushes back on an interruption.',
        exemplars: ['Says "wait, I am not done" and returns to the same drawing after lunch.'],
      },
    ],
  }),

  defineSkill('executiveFunction', 2, {
    key: 'inhibition',
    name: 'Impulse Control',
    description:
      'Stopping the obvious response and doing the required one instead. Underneath waiting, rule games, and staying with work that offers no immediate pull.',
    typicalMonths: [34, 44],
    assessmentHints: [
      'Play freeze dance or red light green light and watch the stop, not the dance.',
      'Try an opposite rule game — say "night" for the sun card — and count how many trials she holds the rule.',
      'Leave something tempting in reach with a "wait until I come back" and see what happens when nobody is watching.',
    ],
    levels: [
      {
        descriptor:
          'Stops an action on "stop" about half the time, and needs an adult close by for it to work.',
        exemplars: ['Halts at the curb when an adult is beside her.'],
      },
      {
        descriptor:
          'Waits a few seconds for a signal before starting, and stops a risky action on a firm word.',
        exemplars: ['Holds still through "ready, steady..." and goes on "go".'],
      },
      {
        descriptor:
          'Plays a simple stop-and-go game, holding still several seconds, and waits her turn to speak after a reminder.',
        exemplars: ['Freezes through a whole round of freeze dance.'],
      },
      {
        descriptor:
          'Follows a rule that runs against the obvious answer for most tries — saying "night" for the sun card, tapping once when the adult taps twice.',
        exemplars: ['Gets six of eight right on the opposite-word game.'],
      },
      {
        descriptor:
          'Holds back a strong want with no adult present, keeps a whisper while excited, and switches to a new rule in the middle of a game.',
        exemplars: ['Leaves the wrapped box alone for the full wait.'],
      },
    ],
  }),

  defineSkill('executiveFunction', 3, {
    key: 'attentionNonPreferred',
    name: 'Attention on Adult-Chosen Activities',
    description:
      'How long she stays with something an adult picked that carries no built-in pull for her. Cycle 1 showed this does not rise when preferred attention rises — it is its own skill, and the reliable lever is making the task genuinely enjoyable rather than lengthening a dull one.',
    typicalMonths: [36, 48],
    prerequisites: [ef('inhibition')],
    assessmentHints: [
      'Time an adult-set activity she did not ask for, and log what the activity was — the content matters more than the minutes.',
      'Run the same skill twice, once inside a game she loves and once plainly, and compare the two times.',
      'Notice whether a first-and-then agreement extends the stretch; that tells you what scaffolding is doing the work.',
    ],
    levels: [
      {
        descriptor:
          'Stays with an adult-chosen task for a minute or two, with an adult right beside her.',
        exemplars: ['Ninety seconds of a sorting task before choosing something else.'],
      },
      {
        descriptor:
          'Two to four minutes on an adult-chosen task while an adult sits with her and keeps it playful.',
        exemplars: ['Three minutes of tracing while an adult makes it into a race.'],
      },
      {
        descriptor:
          'Five to ten minutes on an adult-chosen task when the format is enjoyable, with an occasional nudge back.',
        exemplars: [
          'Five to ten minutes on work she did not choose, longer when it is dressed as a game.',
        ],
      },
      {
        descriptor:
          'Ten to fifteen minutes on an adult-chosen task with no built-in appeal, with a first-and-then agreement in place.',
        exemplars: ['Twelve minutes of letter practice with the promise of drawing after.'],
      },
      {
        descriptor:
          'Sees a plainly unexciting task through — a tidy-up, a practice page — for fifteen minutes or more because it needs doing, with any reward waiting until the end.',
        exemplars: ['Sorts the whole box of blocks away before asking what is next.'],
      },
    ],
  }),

  defineSkill('executiveFunction', 4, {
    key: 'workingMemory',
    name: 'Working Memory',
    description:
      'Holding information in mind and using it — where the pairs are, what the list was, which rule is running now. Feeds multi-step instructions and every game with a rule.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Play a memory pairs game and count the pairs she can handle before it stops being fun.',
      'Send her for two or three named things from another room and see how many come back.',
      'Change the rule mid-game — "now match the colors instead" — and watch whether she updates or keeps the old rule.',
    ],
    levels: [
      {
        descriptor:
          'Finds a toy hidden under one of two cups after a short delay, and remembers where she put something a minute ago.',
        exemplars: ['Goes straight back to where she left the bear.'],
      },
      {
        descriptor:
          'Matches four to six pairs in a memory game, and repeats two words back.',
        exemplars: ['Wins a six-pair game with a few extra flips.'],
      },
      {
        descriptor:
          'Matches around eight pairs, repeats a three-item list back, and fetches two of three named things.',
        exemplars: [
          'Handles about eight pairs in memory and matching games.',
          'Brings the cup and the sock, forgets the banana.',
        ],
      },
      {
        descriptor:
          'Matches ten to twelve pairs, and holds a rule plus an item together — "the blue cup from the kitchen, not the red one" — repeating four items back.',
        exemplars: ['Returns with the correct blue cup, having passed the red one.'],
      },
      {
        descriptor:
          'Updates when the rule changes mid-game — sort by color, now by shape — recalls the day\'s events in order, and repeats a four or five item list reordered.',
        exemplars: [
          'Switches from matching color to matching shape on the first try.',
          'Tells the day back in sequence at bedtime.',
        ],
      },
    ],
  }),

  defineSkill('executiveFunction', 5, {
    key: 'multiStepInstructions',
    name: 'Multi-Step Instructions',
    description:
      'Carrying out several instructions given once, in order. Unrelated steps are the real test — related ones can ride on habit.',
    typicalMonths: [30, 36],
    prerequisites: [ef('workingMemory')],
    assessmentHints: [
      'Give the whole instruction once, then stay quiet — repeating it turns three steps into three separate one-step tasks.',
      'Use unrelated steps so the sequence cannot be guessed from routine.',
      'Ask her to say the plan back before she starts; that is the next step up.',
    ],
    levels: [
      {
        descriptor: 'Follows a one-step instruction supported by a gesture or a look.',
        exemplars: ['Gives you the cup when you ask and point.'],
      },
      {
        descriptor:
          'Follows a two-step instruction where the steps belong together.',
        exemplars: ['Gets her shoes and brings them over.'],
      },
      {
        descriptor:
          'Follows a three-step instruction with unrelated steps, given once.',
        exemplars: [
          'Puts the book on the shelf, closes the door and brings the blue marker.',
          'Handles three to four step sequences.',
        ],
      },
      {
        descriptor:
          'Follows a four-step instruction that contains a condition, and repeats the plan back before starting.',
        exemplars: ['Does "if the box is empty put it away, if not bring it here" correctly.'],
      },
      {
        descriptor:
          'Holds a five-step sequence, carries it out in order after a delay, and can say which step she is on when interrupted.',
        exemplars: ['Comes back from an interruption and says "I still need the towel."'],
      },
    ],
  }),

  defineSkill('executiveFunction', 6, {
    key: 'routineFollowing',
    name: 'Routine Following',
    description:
      'Running a known sequence of daily steps with less and less prompting, and eventually starting it on her own cue.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Count the prompts through one bedtime or getting-ready routine — the count is the measure.',
      'Skip a step on purpose and see whether she notices.',
      'Put up a picture schedule and watch whether she consults it or waits for you.',
    ],
    levels: [
      {
        descriptor:
          'Does a routine step when shown or physically guided through it.',
        exemplars: ['Puts her arm in the sleeve once the sleeve is held open.'],
      },
      {
        descriptor:
          'Does a two-step familiar routine with a reminder at each step.',
        exemplars: ['Shoes off, then coat on, with a prompt for each.'],
      },
      {
        descriptor:
          'Completes a known routine with one or two reminders, using a picture schedule.',
        exemplars: ['Gets through the bedtime routine with reminders.'],
      },
      {
        descriptor:
          'Runs the whole routine from a visual schedule after a single "time for..." cue, and notices a missed step.',
        exemplars: ['Says "we did not brush teeth yet."'],
      },
      {
        descriptor:
          'Starts the routine on her own cue — the timer, the light going dim — keeps the order without a schedule, and adapts it when the day is different.',
        exemplars: ['Begins tidying when the timer sounds, with nobody saying anything.'],
      },
    ],
  }),

  defineSkill('executiveFunction', 7, {
    key: 'taskCompletion',
    name: 'Task Completion',
    description:
      'Getting to the end of what was started, including putting it away. Finishing an adult-set task depends on attention holding for work that carries no pull of its own.',
    typicalMonths: [32, 42],
    prerequisites: [ef('attentionNonPreferred')],
    assessmentHints: [
      'Count finished versus abandoned activities across a day, separating the ones she chose from the ones you chose.',
      'Watch the last ten percent — the final puzzle piece and the tidy-up are where finishing actually lives.',
      'Leave a half-done project out overnight and see whether she returns to it.',
    ],
    levels: [
      {
        descriptor:
          'Finishes a very short task with an adult staying and doing most of it alongside her.',
        exemplars: ['Places the last two pieces while an adult holds the frame.'],
      },
      {
        descriptor:
          'Finishes a two or three minute activity she chose; adult-set ones tend to end partway.',
        exemplars: ['Completes her own tower, leaves the sorting task half done.'],
      },
      {
        descriptor:
          'Finishes most short activities she starts — the last puzzle piece, the whole page — with one "let us finish" prompt.',
        exemplars: ['Colours to the end of the page after a single nudge.'],
      },
      {
        descriptor:
          'Finishes an adult-set task before beginning another, and clears the materials away as part of finishing.',
        exemplars: ['Puts the pens back in the box before asking for the blocks.'],
      },
      {
        descriptor:
          'Returns to an unfinished project later and completes it, and checks her own work against the goal.',
        exemplars: ['Says "it is not done, the roof is missing" and goes back for it.'],
      },
    ],
  }),
]

// ─────────────────────────────────────────────────────────────
// CREATIVE EXPRESSION
// ─────────────────────────────────────────────────────────────

export const CREATIVE_SKILLS: Skill[] = [
  defineSkill('creative', 1, {
    key: 'freeDrawing',
    name: 'Free Drawing',
    description:
      'Drawing from imagination. Follows Lowenfeld\'s sequence: controlled scribble, then scribbles named after the fact, then drawings planned before the pen moves and recognizable without explanation.',
    typicalMonths: [34, 44],
    assessmentHints: [
      'Ask what it is before she says anything, then ask again after — naming before drawing is the level 3 marker.',
      'Show the drawing to someone who was not there and see whether they recognize it unaided.',
      'Count the correct parts on an animal or a house rather than judging the overall look.',
    ],
    levels: [
      {
        descriptor:
          'Makes deliberate scribbles — circles, lines, zigzags — filling the page, without naming them.',
        exemplars: ['Loops and lines covering a sheet, made with obvious intent.'],
      },
      {
        descriptor:
          'Names the scribble after making it, and the name may change on a second telling; makes closed shapes she calls things.',
        exemplars: ['Says "that is a dog", then calls the same mark a car a minute later.'],
      },
      {
        descriptor:
          'Says what she will draw before drawing it, and the result is recognizable to an adult without being told — a person as a head with legs, a sun, a house.',
        exemplars: ['Announces "a house" and draws a square with a triangle on top.'],
      },
      {
        descriptor:
          'Draws several different subjects on request, each with three or more correct parts.',
        exemplars: [
          'A cat with ears, whiskers and a tail.',
          'A house with a door, windows and a roof.',
        ],
      },
      {
        descriptor:
          'Draws constantly from imagination with details others notice — fingers, patterned clothes, animals built correctly — adding parts because the story needs them, not because she was asked.',
        exemplars: [
          'Detailed cats and houses, drawn prolifically and on every surface available.',
          'Adds fingers and clothing details unprompted.',
        ],
      },
    ],
  }),

  defineSkill('creative', 2, {
    key: 'faceDrawingFrontal',
    name: 'Drawing Faces (Front View)',
    description:
      'The face seen head-on, scored the way Draw-A-Person scoring does it: which parts are present and whether they sit where they belong.',
    typicalMonths: [34, 42],
    prerequisites: [cr('freeDrawing')],
    assessmentHints: [
      'Ask for a picture of a person in the family and count the parts rather than judging the likeness.',
      'Check whether the features sit inside the head outline; outside is an earlier stage, not a mistake.',
      'Ask for a happy one and a sad one, and see whether anything actually changes.',
    ],
    levels: [
      {
        descriptor:
          'Draws a closed circle and calls it a face, with no features or one stray mark.',
        exemplars: ['A single loop pointed at and named "mommy".'],
      },
      {
        descriptor:
          'Circle with two marks for eyes and sometimes a mouth; features may drift outside the outline.',
        exemplars: ['Two dots and a line, one of them sitting on the edge.'],
      },
      {
        descriptor:
          'Circle with eyes, mouth and nose all inside and roughly placed, hair as lines on top; legs may still come straight off the head.',
        exemplars: ['A head with four features and two legs attached directly to it.'],
      },
      {
        descriptor:
          'Five or more parts in the right places — eyes with pupils, eyebrows, nose, mouth, ears, hair — and a neck joining head to body.',
        exemplars: ['A person with a neck, hair and pupils inside the eyes.'],
      },
      {
        descriptor:
          'Faces carry expression on purpose — the mouth curves up or down, eyes widen for surprise — and different people look different.',
        exemplars: ['Long hair for one person, glasses for another, in the same picture.'],
      },
    ],
  }),

  defineSkill('creative', 3, {
    key: 'faceDrawingProfile',
    name: 'Drawing Faces (Side View)',
    description:
      'The side view. Genuinely hard, because it means drawing only what is visible rather than everything she knows is there — most children keep both eyes in a profile until well past this age.',
    typicalMonths: [48, 60],
    prerequisites: [cr('faceDrawingFrontal')],
    assessmentHints: [
      'Ask for someone "looking that way" and see what changes.',
      'Count the eyes in the profile; one eye is the marker that she is drawing what she sees.',
      'Turn your own head sideways as a live model and let her look back and forth.',
    ],
    levels: [
      {
        descriptor: 'Draws a front-facing face when asked for a side view.',
        exemplars: ['The usual circle with two eyes, regardless of the request.'],
      },
      {
        descriptor:
          'Draws a front-facing face but adds one side detail — an ear sticking out, hair on one side only.',
        exemplars: ['Front face with all the hair swept to the left.'],
      },
      {
        descriptor:
          'Draws a side-facing head outline with one eye and a mouth at the edge, though the nose may still point forward.',
        exemplars: ['An egg-shaped head facing left, one eye, mouth on the edge.'],
      },
      {
        descriptor:
          'Profile with a nose bump on the outline, one eye, one ear and the mouth at the edge — the head clearly faces one way.',
        exemplars: ['A recognizable profile with a nose in the silhouette.'],
      },
      {
        descriptor:
          'Profile with the body turned the same way — shoulders, one arm — and the character looking at something else in the picture.',
        exemplars: ['A side-on girl looking at the cat she also drew.'],
      },
    ],
  }),

  defineSkill('creative', 4, {
    key: 'observationalDrawing',
    name: 'Observational Drawing',
    description:
      'Drawing a real thing that is in front of her. The whole skill is the looking: the shift from drawing what she knows about an object to drawing what is actually visible from where she sits.',
    typicalMonths: [40, 50],
    prerequisites: [cr('freeDrawing')],
    assessmentHints: [
      'Put one real object between her and the paper and count how many times she looks up.',
      'Ask about a specific feature afterwards — how many petals, which side the leaf is on — and check the drawing.',
      'Set a cup with the handle turned away and see whether she draws the handle anyway; drawing it anyway is normal and expected here.',
    ],
    levels: [
      {
        descriptor:
          'Looks at the object, then makes marks that have nothing to do with it.',
        exemplars: ['Studies the flower, then draws her usual loops.'],
      },
      {
        descriptor:
          'Looks at the object while drawing and copies one big feature, usually the overall shape.',
        exemplars: ['Draws a round blob for an apple while looking at the apple.'],
      },
      {
        descriptor:
          'Gets the object\'s main shape plus two features in roughly the right places.',
        exemplars: ['A flower with a round center, petals around it and a stem going down.'],
      },
      {
        descriptor:
          'Looks back at the object several times during the drawing and adds what she notices there.',
        exemplars: ['Adds the right number of petals, or the chip on the rim of the cup.'],
      },
      {
        descriptor:
          'Draws what is visible rather than what she knows: a half-hidden object drawn half hidden, a cup from the side without a round rim on top.',
        exemplars: ['Leaves out the handle when the handle is turned away.'],
      },
    ],
  }),

  defineSkill('creative', 5, {
    key: 'composition',
    name: 'Composition',
    description:
      'How the page gets organized. Runs from marks landing anywhere, through several things arranged in relation to each other, to a real scene with a ground and a sky.',
    typicalMonths: [42, 54],
    prerequisites: [cr('freeDrawing')],
    assessmentHints: [
      'Ask her to tell you about the picture and listen for words about position — next to, behind, on top.',
      'Look for a ground line, or for things resting on the bottom edge; that is the scene arriving.',
      'Ask "where will the sun go?" before she draws it and see whether she has a plan.',
    ],
    levels: [
      {
        descriptor:
          'Marks land anywhere and overlap; the page is a surface rather than a scene.',
        exemplars: ['Drawing continues off the edge of the paper onto the table.'],
      },
      {
        descriptor:
          'One thing per page, usually large and near the middle.',
        exemplars: ['A single big cat filling the sheet.'],
      },
      {
        descriptor:
          'Two or three separate things on one page, with a stated relationship between them, though they float without a ground.',
        exemplars: ['Says "the cat is next to the house" — both drawn mid-air.'],
      },
      {
        descriptor:
          'Uses the page as a scene: things sit on a ground line or the bottom edge, sky items go at the top, sizes are roughly sensible.',
        exemplars: ['Grass line along the bottom with the house standing on it.'],
      },
      {
        descriptor:
          'Composes deliberately — a large subject in front, smaller things behind, a filled background — and decides the layout before drawing.',
        exemplars: ['Says "the house goes here so there is room for the tree" before starting.'],
      },
    ],
  }),

  defineSkill('creative', 6, {
    key: 'mediaRange',
    name: 'Range of Media',
    description:
      'How many materials she works in and how well she handles each one on its own terms — a brush is dipped and wiped, glue is dotted, clay is pressed and joined.',
    typicalMonths: [32, 42],
    assessmentHints: [
      'Count the different materials used across two weeks, not one session.',
      'Offer a messy option alongside a clean one and see which she reaches for.',
      'Ask her which material she wants for a particular idea — choosing on purpose is the level 4 marker.',
    ],
    levels: [
      {
        descriptor:
          'Uses one or two familiar tools, usually crayon or a chunky marker, and keeps away from messy materials.',
        exemplars: ['Chooses crayons every time; declines finger paint.'],
      },
      {
        descriptor:
          'Uses three or four materials happily, including at least one messy one.',
        exemplars: ['Crayon, marker, playdough and finger paint all in use.'],
      },
      {
        descriptor:
          'Works in five or more materials and handles each tool the way it wants to be handled.',
        exemplars: [
          'Watercolor and collage both tried and enjoyed.',
          'Dips and wipes the brush rather than flooding the paper.',
        ],
      },
      {
        descriptor:
          'Picks the material to suit the idea and combines two of them in one piece.',
        exemplars: ['Says "I need paint for the sky" and adds cut paper on top of it.'],
      },
      {
        descriptor:
          'Works in three dimensions and in print — clay figures that stand, stamped repeats, mixed media — and adjusts when the material behaves unexpectedly.',
        exemplars: ['Adds a fatter base when the clay figure will not stand up.'],
      },
    ],
  }),

  defineSkill('creative', 7, {
    key: 'pretendPlay',
    name: 'Pretend Play',
    description:
      'Symbolic play: standing one thing in for another, taking a role, and running a story through it. Vygotsky treated this as the setting where a child performs above their everyday level, which is why it carries so much other learning.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Hand her a plain block during play and watch whether it becomes something.',
      'Time the longest single scenario and note whether it has an ending or just stops.',
      'Ask "what happens next?" mid-scene and listen for a problem and a resolution.',
    ],
    levels: [
      {
        descriptor:
          'Acts out a familiar routine on herself or a doll using realistic props.',
        exemplars: ['Pretends to drink from an empty toy cup.'],
      },
      {
        descriptor:
          'Lets one object stand for another and strings two or three pretend actions together.',
        exemplars: ['A block becomes a phone; she dials, talks, hangs up.'],
      },
      {
        descriptor:
          'Plays out a small scenario with a beginning and an end, and names her role.',
        exemplars: ['The doll is sick, gets medicine, gets better. "I am the doctor."'],
      },
      {
        descriptor:
          'Keeps a scenario going ten minutes or more with several roles, hands a role to an adult, and says what the characters want and feel.',
        exemplars: [
          'Multi-step stories with assigned roles that hold together to the end.',
          'Says "you are the patient and you are scared of the needle."',
        ],
      },
      {
        descriptor:
          'Builds an invented world across several sessions with the same characters and rules, introduces a problem on purpose and resolves it, and steers the play from outside while staying in role.',
        exemplars: ['Picks up yesterday\'s clinic story with the same patients by name.'],
      },
    ],
  }),

  defineSkill('creative', 8, {
    key: 'bookMaking',
    name: 'Book Making',
    description:
      'Making her own books as author and illustrator — drawing, writing with invented spelling, sequencing the pages, and reading it aloud. The keystone activity, because it pulls drawing, writing, story and reading into one object she owns.',
    typicalMonths: [40, 52],
    prerequisites: [cr('freeDrawing'), cr('pretendPlay')],
    assessmentHints: [
      'Ask her to read the finished book twice, a day apart, and check whether the story stays the same.',
      'Never correct the spelling — count whether the letters she wrote match sounds in the word.',
      'Watch how she handles the object: front cover first, pages left to right, top line first.',
    ],
    levels: [
      {
        descriptor:
          'Turns pages and draws on them; the pages have no relationship to each other.',
        exemplars: ['A stapled stack with unrelated scribbles.'],
      },
      {
        descriptor:
          'Draws on several pages and calls the result a book, telling a different story each time she reads it.',
        exemplars: ['Two different stories from the same three pages, an hour apart.'],
      },
      {
        descriptor:
          'Makes a book of about three pages whose drawings stay in order, and tells the same story on a second reading.',
        exemplars: ['Reads it back the next day with the same events in the same order.'],
      },
      {
        descriptor:
          'The book has a beginning, a middle and an end, with letters or words she wrote as labels — invented spelling counts fully — and a title on the front.',
        exemplars: [
          'Labels a drawing KAT and a picture of a house HOWS.',
          'Writes AURORA on the cover as the author.',
        ],
      },
      {
        descriptor:
          'Plans the book before making it, writes a caption or a sentence on each page, keeps a character consistent throughout, and reads it aloud to an audience.',
        exemplars: ['Reads her finished book to a younger cousin, holding it out for them to see.'],
      },
    ],
  }),

  defineSkill('creative', 9, {
    key: 'building',
    name: 'Building & Construction',
    description:
      'Three-dimensional construction, following the classic block sequence: stacks, then rows, then bridges with a spanned gap, then enclosures, then structures built to serve a purpose.',
    typicalMonths: [32, 42],
    assessmentHints: [
      'Ask what it is going to be before she starts; announcing the plan first is the level 3 marker.',
      'Look for a bridge — two supports with a piece laid across a gap — and for a closed enclosure.',
      'Knock over a piece by accident and watch whether she repairs it or abandons the build.',
    ],
    levels: [
      {
        descriptor: 'Stacks four to six blocks and knocks them down, with no plan.',
        exemplars: ['Five blocks up, then a swipe.'],
      },
      {
        descriptor: 'Builds towers and rows, then names them once they are finished.',
        exemplars: ['A long line of blocks called a train after the fact.'],
      },
      {
        descriptor:
          'States the purpose first and builds it, including an enclosure or a bridge with a gap spanned.',
        exemplars: ['Says "a garage for the car" and builds walls with a roof across.'],
      },
      {
        descriptor:
          'Builds a structure with several parts — rooms or levels — that serves the play, and repairs it when it falls.',
        exemplars: [
          'Enclosures and rooms built with a clear intent, then played in.',
          'Rebuilds the wall that collapsed instead of starting over.',
        ],
      },
      {
        descriptor:
          'Plans before building, solves a structural problem such as widening the base or adding a support, and adapts the design to a constraint.',
        exemplars: ['Says "it has to be tall enough for the giraffe" and rebuilds higher.'],
      },
    ],
  }),

  defineSkill('creative', 10, {
    key: 'musicSinging',
    name: 'Singing',
    description:
      'Songs from memory, and then songs of her own. Children hold the up-and-down shape of a tune before they hold exact notes, so contour comes first and accuracy later.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Start a familiar song and stop; see whether she carries it on alone.',
      'Listen for the shape of the tune going up and down in the right places, before worrying about the notes.',
      'Change one word of a known song and see whether she notices or joins the game.',
    ],
    levels: [
      {
        descriptor:
          'Joins in on the last word or the hand motion of a familiar song.',
        exemplars: ['Supplies "star" at the end of the line.'],
      },
      {
        descriptor:
          'Sings the chorus of several songs with most of the words, the tune loosely followed.',
        exemplars: ['Sings the chorus while an adult carries the verses.'],
      },
      {
        descriptor:
          'Sings a whole short song from memory with most words in order, and follows the tune\'s up-and-down shape.',
        exemplars: [
          'Knows the lyrics of favorite songs and sings them through.',
        ],
      },
      {
        descriptor:
          'Sings five or more songs accurately, stays close to the tune, and makes up her own words to a tune she knows.',
        exemplars: ['New verses about the cat, sung to a familiar melody.'],
      },
      {
        descriptor:
          'Invents songs with her own words and tune, sings for an audience on purpose, and changes loud or soft, fast or slow, when asked.',
        exemplars: ['Performs a made-up song for the family and takes a bow.'],
      },
    ],
  }),

  defineSkill('creative', 11, {
    key: 'musicRhythm',
    name: 'Rhythm & Beat',
    description:
      'Matching movement to an outside beat. Harder than it looks before four, because it means giving up her own tempo and locking onto someone else\'s.',
    typicalMonths: [36, 48],
    assessmentHints: [
      'Clap a steady beat and count how many of her claps land with yours, rather than whether she is clapping.',
      'Change the music from fast to slow mid-song and see whether her movement changes with it.',
      'Clap a short pattern and ask her to send it back.',
    ],
    levels: [
      {
        descriptor:
          'Moves to music with a general bounce that does not line up with the beat.',
        exemplars: ['Bobs and spins happily, off the beat.'],
      },
      {
        descriptor:
          'Claps or bangs along and falls in with the beat for a few beats at a time.',
        exemplars: ['Three or four drum hits in time before drifting.'],
      },
      {
        descriptor:
          'Holds a steady beat on a drum or with claps for eight beats or more, and changes speed when the music does.',
        exemplars: [
          'Moves rhythmically to music she knows and follows a tempo change.',
        ],
      },
      {
        descriptor:
          'Copies a short rhythm pattern back, and keeps her own part while somebody else plays a different one.',
        exemplars: ['Returns clap-clap-rest-clap correctly.'],
      },
      {
        descriptor:
          'Keeps a steady beat while singing at the same time, marks the strong beats, and holds a repeating pattern in a group.',
        exemplars: ['Drums the pulse through a whole song she is also singing.'],
      },
    ],
  }),
]

// ─────────────────────────────────────────────────────────────
// Aggregates
// ─────────────────────────────────────────────────────────────

export const SOCIAL_EXECUTIVE_CREATIVE_DOMAINS: DomainDef[] = [
  SOCIAL_EMOTIONAL_DOMAIN,
  EXECUTIVE_FUNCTION_DOMAIN,
  CREATIVE_DOMAIN,
]

export const SOCIAL_EXECUTIVE_CREATIVE_SKILLS: Skill[] = [
  ...SOCIAL_EMOTIONAL_SKILLS,
  ...EXECUTIVE_FUNCTION_SKILLS,
  ...CREATIVE_SKILLS,
]
